import { useCallback } from 'react';
import { useInstanceId, useOpenDeeplink } from 'shared/src/hooks';
import { useRootStackNavigation } from 'shared/src/hooks/useRootNavigation';
import { bridge, isNotNil } from 'shared/src/utils';

type WebViewMessage = {
  type: string;
  body?: string | object;
};

export const useWebViewActionFromMessage = () => {
  const navigation = useRootStackNavigation();
  const { openDeeplink } = useOpenDeeplink();
  const instanceId = useInstanceId();

  const convertEventToData = useCallback<
    (params: { nativeEvent: { data?: string } }) => WebViewMessage | null
  >(({ nativeEvent }) => {
    try {
      const message = JSON.parse(nativeEvent.data ?? '{}');

      if (typeof message.type === 'undefined') return null;

      return message;
    } catch {
      return null;
    }
  }, []);

  const convertToObject = useCallback((body?: string | object) => {
    if (isNotNil(body)) {
      try {
        return typeof body === 'string' ? JSON.parse(body) : body;
      } catch {
        return null;
      }
    }

    return null;
  }, []);

  return useCallback<(params: { nativeEvent: { data?: string } }) => void>(
    event => {
      const message = convertEventToData(event);
      if (isNotNil(message)) {
        switch (message.type) {
          case 'closeWebview': {
            const canGoBack = navigation.canGoBack();

            if (canGoBack) {
              navigation.goBack();
            } else {
              bridge.closeInstance({ instanceId });
            }

            const body = convertToObject(message.body);
            if (isNotNil(body) && isNotNil(body.afterDeeplink)) {
              openDeeplink(body.afterDeeplink);
            }

            return;
          }
        }
      }
    },
    [convertEventToData, navigation, convertToObject, instanceId, openDeeplink],
  );
};
